import { logout } from "./slices/auth";
import { store } from "./store";
import { generateUUID } from "./uuid";

import { BASEPATH } from "../config/env";
import { posthogClient } from "../config/posthog";
import { loginRoute } from "../routes/login/route";

// why: reading storage throws SecurityError rather than returning null when it is
// blocked (private mode, third-party-cookie blocking, sandboxed iframe). smartFetch
// calls this on every authenticated request, so an unguarded read took down the
// whole data layer instead of degrading to an unauthenticated request.
export const getAuthToken = () => {
  let localToken: string | null = null;

  try {
    localToken = window.localStorage.getItem("token");
  } catch {
    // Blocked storage means "no token"; the caller falls back to anon access.
  }

  if (localToken != null) {
    return `Bearer ${localToken}`;
  }

  return null;
};

// why: writing to storage throws SecurityError exactly like reading it when
// storage is blocked. This clear runs on the line before the token-refresh
// redirect, so an unguarded throw aborted smartFetch with a SecurityError and
// the re-auth navigation the server asked for never fired — the caller got a
// storage error where it expected a redirect. Blocked storage degrades to
// "token not cleared" and the redirect still runs.
const clearAuthToken = () => {
  try {
    window.localStorage.removeItem("token");
  } catch {
    // Nothing to clear; the refreshed token in the URL supersedes it anyway.
  }
};

// Datadog trace ID, unique per document load
export const traceId = generateUUID();

export async function smartFetch(
  url: string,
  requestOpts: RequestInit = {},
  askAuth?: boolean,
  opts: Record<string, string | null> = {},
  trials = 3,
) {
  // Check if we're in the middle of logging out - if so, don't make API calls
  const { auth } = store.getState();
  if (auth.loggingOut) {
    throw new Error("Request cancelled - user is logging out");
  }

  // Check path segments (not substring) so branch/env names like
  // "ask-puntt-drag-drop-upload" don't accidentally trigger the auth requirement
  // for unauthenticated endpoints such as /login-workos on epic environments.
  // `/file` is the post-cutover christo upload mount (rest*.puntt.ai/file and
  // epic HTTP API /file). `/upload` remains for the pre-cutover drain URL.
  let pathSegments: string[] = [];
  try {
    pathSegments = new URL(url).pathname.split("/");
  } catch {
    // Fallback for relative URLs — keep old behavior
    pathSegments = url.split("/");
  }
  // why: post-cutover VITE_UPLOAD_API_URL is $CHRISTO_HTTPAPI_URL/file, not /upload — dropping this 401s Ask Puntt chips on HTTP-API epics.
  const requiresAuth =
    pathSegments.includes("private") ||
    pathSegments.includes("upload") ||
    pathSegments.includes("file");
  const defaultHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    "x-datadog-trace-id": traceId,
    "x-datadog-parent-id": "0", // 0 indicates this is the root span
    ...(requestOpts.headers as Record<string, string>),
  };
  requestOpts.mode = "cors";
  requestOpts.credentials = "include";

  if ((!opts.token && requiresAuth) || askAuth) {
    const localToken = getAuthToken();

    if (localToken == null) {
      throw new Error(
        "Attempt to access private route without an auth token is forbidden.",
      );
    }

    defaultHeaders.Authorization = localToken;
  } else if (opts.token) {
    defaultHeaders.Authorization = `Bearer ${opts.token}`;
  }

  requestOpts.headers = defaultHeaders;

  // Make the request and retry if it fails with a network error
  let response: Response;
  try {
    response = await fetch(url, requestOpts);
    if (!response.status && requestOpts.method === "GET" && trials > 0) {
      console.warn("Failed to fetch; retrying GET request");
      return smartFetch(url, requestOpts, askAuth, opts, trials - 1);
    }
  } catch (e) {
    if (
      (e as Error).message.indexOf("fetch") >= 0 &&
      requestOpts.method === "GET" &&
      trials > 0
    ) {
      console.warn("Failed to fetch; retrying GET request");
      return smartFetch(url, requestOpts, askAuth, opts, trials - 1);
    } else {
      throw e;
    }
  }

  if (!response.ok) {
    // Token expired
    if (response.status === 401) {
      const responseData = (await response.clone().json()) as { error: string };
      if (responseData.error === "Invalid Token") {
        store.dispatch(logout());
        posthogClient.reset();
        const basepath = BASEPATH === "/" ? "" : BASEPATH;
        window.location.assign(
          basepath + loginRoute.to + "?error=session-expired",
        );
      }
    }

    throw response;
  }

  const refreshedToken = response.headers.get("X-Refreshed-Token");
  if (refreshedToken) {
    const url = new URL(window.location.href);
    url.searchParams.set("refreshedToken", refreshedToken);
    clearAuthToken();
    window.location.href = url.toString();
    return;
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
}

// why: decodeURIComponent throws URIError on a raw '%' not followed by two hex
// digits (an asset named "50% off.png"), and new URL throws TypeError on a
// relative or non-http url. downloadAsset is called without await at several
// call sites, so the throw surfaced as an unhandled rejection and the download
// silently never started while the caller had already set its loading state.
function fileNameFromUrl(url: string): string {
  try {
    return decodeURIComponent(
      new URL(url).pathname.split("/").pop() || "download",
    );
  } catch {
    return "download";
  }
}

export async function downloadAsset(
  url: string,
  fileName?: string,
  analyticsEventName = "downloaded_asset",
  analyticsEventProperties: Record<string, boolean | number | string> = {},
) {
  const startTime = Date.now();
  // Fetch the file instead of just downloading the file directly so that the
  // appropriate headers are set for AssetShield.
  const response = await fetch(url);
  const fileType = response.headers.get("Content-Type");

  // Extract the filename from the Content-Disposition header if available, and
  // from the URL if not
  const contentDisposition = response.headers.get("Content-Disposition");
  const serverFileName = contentDisposition?.match(/filename="(.+)"/i)?.[1];
  fileName ||= serverFileName || fileNameFromUrl(url);

  if (!response.ok) {
    posthogClient.capture(analyticsEventName + "_error", {
      assetUrl: url,
      assetName: fileName,
      fileType,
      durationSeconds: (Date.now() - startTime) / 1000,
      statusCode: response.status,
      statusText: response.statusText,
      responseText: await response.text(),
      ...analyticsEventProperties,
    });
    throw new Error("Failed to download asset");
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.style.display = "none";
  link.download = fileName;
  link.href = blobUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  posthogClient.capture(analyticsEventName, {
    assetUrl: url,
    assetName: fileName,
    fileType,
    fileSizeMB: blob.size / 1024 / 1024,
    durationSeconds: (Date.now() - startTime) / 1000,
    ...analyticsEventProperties,
  });

  // Ensure the download starts before we release the memory
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

export const handleApiError = async (e: unknown) => {
  if (e instanceof Response) {
    const error = e as Response;
    const errorData = (await error.clone().json()) as {
      message?: string;
    };
    const errorMessage = errorData.message || "An unknown error occurred";

    throw new Error(errorMessage);
  } else {
    console.error("Error is not a Response:", e);
    throw e;
  }
};

export const errorMessage = async (error: unknown): Promise<string> => {
  if (error instanceof Response) {
    try {
      const errorData = (await error.clone().json()) as {
        message?: string;
      };
      return typeof errorData.message === "string"
        ? errorData.message
        : JSON.stringify(errorData);
    } catch {
      // If JSON parsing fails, try to handle XML (e.g. from S3 errors)
      const text = await error.clone().text();
      const messageMatch = text.match(/<Message>(.+?)<\/Message>/)?.[1];
      return messageMatch ?? text;
    }
  } else if (error instanceof Error) {
    if (error.message === "Failed to fetch") {
      return "Either the server had a hiccup or there was a problem with your internet connection. Please try again.";
    }
    if (error.cause instanceof Error) {
      return `${error.message} (caused by: ${errorMessage(error.cause)})`;
    }
    return error.message;
  } else if (!String(error).startsWith("[object ")) {
    return String(error);
  }
  return "An unknown error occurred";
};
